import React, { useEffect, useState, useCallback } from "react";
import { Link, useLocation, useHistory } from "react-router-dom";
import { debounce } from "lodash";
import { toast } from "react-toastify";

// Services & Types
import { OfferingService } from "services/offering.service";
import { IOfferingResponse } from "models/church.types";

export default function OfferingList() {
  const [offerings, setOfferings] = useState<IOfferingResponse[]>([]);
  const [loading, setLoading] = useState<boolean>(false);
  const [searchTerm, setSearchTerm] = useState("");

  const location = useLocation();
  const history = useHistory();

  const queryParams = new URLSearchParams(location.search);
  const eventIdFromUrl = queryParams.get("eventId");

  const [currentPage, setCurrentPage] = useState(0);
  const [totalItems, setTotalItems] = useState(0);

  const itemsPerPage = 10;

  // Calcul du total de la page actuelle
  const totalAmount = offerings.reduce(
    (acc, curr) => acc + (curr.amount || 0),
    0,
  );

  // ============================================================
  // CALCUL DU NOMBRE TOTAL DE PAGES
  // ============================================================

  const totalPages = Math.ceil(totalItems / itemsPerPage);

  // ============================================================
  // CHARGEMENT DES DONNÉES
  // ============================================================

  const loadData = async (query: string, page: number) => {
    setLoading(true);

    try {
      let result;

      if (eventIdFromUrl && !query.trim()) {
        result = await OfferingService.getByEvent(
          Number(eventIdFromUrl),
          page,
          itemsPerPage,
        );
      } else if (query.trim()) {
        result = await OfferingService.search(
          query,
          page,
          itemsPerPage,
        );
      } else {
        result = await OfferingService.getAll(
          page,
          itemsPerPage,
        );
      }

      setOfferings(result.data || []);
      setTotalItems(result.totalCount || 0);
    } catch (error) {
      console.error("Erreur de chargement des offrandes :", error);

      toast.error("Impossible de charger les offrandes");
    } finally {
      setLoading(false);
    }
  };

  // ============================================================
  // CHARGEMENT INITIAL + PAGINATION
  // ============================================================

  useEffect(() => {
    loadData(searchTerm, currentPage);
  }, [currentPage, eventIdFromUrl]);

  // ============================================================
  // RECHERCHE AVEC DEBOUNCE
  // ============================================================

  const debouncedSearch = useCallback(
    debounce((value: string) => {
      setCurrentPage(0);
      loadData(value, 0);
    }, 500),
    [eventIdFromUrl],
  );

  // ============================================================
  // CHANGEMENT DE LA RECHERCHE
  // ============================================================

  const onSearchChange = (
    e: React.ChangeEvent<HTMLInputElement>,
  ) => {
    const value = e.target.value;

    setSearchTerm(value);

    debouncedSearch(value);
  };

  // ============================================================
  // SUPPRESSION
  // ============================================================

  const handleDelete = async (id: number) => {
    if (
      window.confirm(
        "Supprimer cet enregistrement d'offrande ?",
      )
    ) {
      try {
        setLoading(true);

        await OfferingService.delete(id);

        toast.success("Supprimé avec succès");

        await loadData(searchTerm, currentPage);
      } catch (error) {
        console.error(
          "Erreur lors de la suppression de l'offrande :",
          error,
        );

        toast.error("Erreur lors de la suppression.");
      } finally {
        setLoading(false);
      }
    }
  };

  // ============================================================
  // FORMATAGE MONÉTAIRE
  // ============================================================

  const formatCurrency = (amount: number) => {
    return new Intl.NumberFormat("fr-FR", {
      style: "currency",
      currency: "XOF",
    }).format(amount);
  };

  // ============================================================
  // RENDER
  // ============================================================

  return (
    <div className="flex flex-col w-full px-6 min-h-screen">

      {/* ======================================================
          HEADER
      ====================================================== */}

      <div className="mb-6 pt-8">
        <h2 className="text-white text-3xl font-bold uppercase tracking-tight flex items-center gap-3">
          <i className="fas fa-hand-holding-heart text-[#D4AF37]"></i>
          Registre des Offrandes
        </h2>

        <p className="text-slate-400 text-sm italic">
          Suivi des contributions financières et offrandes
        </p>
      </div>

      {/* ======================================================
          BARRE DE RECHERCHE + BOUTON
      ====================================================== */}

      <div className="flex flex-col md:flex-row items-center justify-between gap-4 mb-8">

        <div className="flex items-center gap-2 w-full md:w-auto">

          <div className="relative w-full md:w-96 group">

            <span className="absolute inset-y-0 left-0 pl-4 flex items-center text-slate-400 group-focus-within:text-[#D4AF37] transition-colors">

              {loading ? (
                <i className="fas fa-circle-notch animate-spin text-[#D4AF37]"></i>
              ) : (
                <i className="fas fa-search"></i>
              )}

            </span>

            <input
              type="text"
              value={searchTerm}
              onChange={onSearchChange}
              placeholder="Rechercher..."
              className="w-full bg-slate-800 border border-slate-700 text-white text-sm rounded-lg pl-11 pr-4 py-2 font-sans h-[38px] outline-none focus:border-blue-500 transition-colors"
            />

          </div>

        </div>

        <Link
          to="/admin/offerings/register"
          className="bg-[#D4AF37] hover:bg-[#b8952e] text-slate-900 font-bold px-8 h-[38px] flex items-center justify-center rounded-lg whitespace-nowrap transition-colors w-full lg:w-auto text-center text-sm shadow-md hover:shadow-lg"
        >
          <i className="fas fa-plus mr-2"></i>
          Ajouter une offrande
        </Link>

      </div>

      {/* ======================================================
          TABLEAU
      ====================================================== */}

      <div className="bg-slate-800 border border-slate-700 rounded-2xl overflow-hidden shadow-2xl mb-10">

        <div className="overflow-x-auto">

          <table className="w-full text-left border-collapse">

            {/* ==================================================
                HEADER DU TABLEAU
            ================================================== */}

            <thead>

              <tr className="bg-[#1E293B] text-slate-300 uppercase text-[10px] font-black tracking-widest border-b border-slate-700">

                <th className="px-6 py-5">
                  Programme
                </th>

                <th className="px-6 py-5 text-center">
                  Date
                </th>

                <th className="px-6 py-5">
                  Description
                </th>

                <th className="px-6 py-5">
                  Caisse
                </th>

                <th className="px-6 py-5 text-right">
                  Montant
                </th>

                <th className="px-6 py-5 text-center">
                  Actions
                </th>

              </tr>

            </thead>

            {/* ==================================================
                CORPS DU TABLEAU
            ================================================== */}

            <tbody className="divide-y divide-slate-700/50">

              {offerings.length === 0 && !loading ? (

                <tr>

                  <td
                    colSpan={6}
                    className="text-center py-16 text-slate-500 italic text-sm"
                  >

                    <i className="fas fa-box-open block text-3xl mb-3 opacity-20"></i>

                    Aucune offrande trouvée.

                  </td>

                </tr>

              ) : (

                offerings.map((off) => (

                  <tr
                    key={off.id}
                    className="hover:bg-slate-700/40 transition-colors group"
                  >

                    {/* ==========================================
                        PROGRAMME
                    ========================================== */}

                    <td className="px-6 py-4 text-sm text-white font-bold">
                      {off.eventTitle || "N/A"}
                    </td>

                    {/* ==========================================
                        DATE
                    ========================================== */}

                    <td className="px-6 py-4 text-xs text-slate-400 text-center whitespace-nowrap">
                      {off.offeringDate
                        ? new Date(
                            off.offeringDate,
                          ).toLocaleDateString("fr-FR", {
                            day: "2-digit",
                            month: "2-digit",
                            year: "numeric",
                          })
                        : "-"}
                    </td>

                    {/* ==========================================
                        DESCRIPTION
                    ========================================== */}

                    <td className="px-6 py-4 text-xs text-slate-400 max-w-xs truncate">
                      {off.description || (
                        <span className="text-slate-600 italic">
                          Aucune description
                        </span>
                      )}
                    </td>

                    {/* ==========================================
                        CAISSE
                    ========================================== */}

                    <td className="px-6 py-4 whitespace-nowrap">

                      {off.fundName ? (

                        <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-slate-900 text-[#D4AF37] border border-slate-700 shadow-sm">

                          <i className="fas fa-wallet mr-1.5 text-[10px]"></i>

                          {off.fundName}

                        </span>

                      ) : (

                        <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-slate-700/50 text-slate-400 border border-slate-600/40">

                          <i className="fas fa-exclamation-circle mr-1.5 text-[10px] text-amber-500/70"></i>

                          Non rattachée

                        </span>

                      )}

                    </td>

                    {/* ==========================================
                        MONTANT
                    ========================================== */}

                    <td className="px-6 py-4 text-right font-bold text-white whitespace-nowrap">

                      {formatCurrency(off.amount)}

                    </td>

                    {/* ==========================================
                        ACTIONS
                    ========================================== */}

                    <td className="px-6 py-4 text-center whitespace-nowrap">

                      <div className="flex justify-center gap-4">

                        <Link
                          to={`/admin/offerings/edit/${off.id}`}
                          className="text-slate-400 hover:text-[#D4AF37] transition-colors"
                          title="Modifier"
                        >
                          <i className="fas fa-edit"></i>
                        </Link>

                        <button
                          onClick={() => handleDelete(off.id)}
                          className="text-slate-400 hover:text-red-500 transition-colors"
                          title="Supprimer"
                        >
                          <i className="fas fa-trash-alt"></i>
                        </button>

                      </div>

                    </td>

                  </tr>

                ))

              )}

            </tbody>

            {/* ==================================================
                FOOTER : TOTAL
            ================================================== */}

            <tfoot className="bg-[#1E293B] border-t border-slate-700">

              <tr>

                <td
                  colSpan={4}
                  className="px-6 py-4 text-right font-black text-slate-300 uppercase text-[10px] tracking-wider"
                >
                  Total Page
                </td>

                <td className="px-6 py-4 text-right font-black text-[#D4AF37]">
                  {formatCurrency(totalAmount)}
                </td>

                <td></td>

              </tr>

            </tfoot>

          </table>

        </div>

        {/* ======================================================
            PAGINATION
        ====================================================== */}

        <div className="px-6 py-5 bg-[#0F172A]/50 border-t border-slate-700 flex items-center justify-between">

          <p className="text-[11px] text-slate-500 font-bold uppercase tracking-wider">

            Total global :

            <span className="text-[#D4AF37] ml-1">
              {totalItems}
            </span>

            <span className="ml-1">
              offrandes
            </span>

          </p>

          <div className="flex gap-2">

            {/* PRÉCÉDENT */}

            <button
              disabled={currentPage === 0}
              onClick={() =>
                setCurrentPage((p) => p - 1)
              }
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Précédent
            </button>

            {/* SUIVANT */}

            <button
              disabled={
                currentPage + 1 >= totalPages ||
                totalPages === 0
              }
              onClick={() =>
                setCurrentPage((p) => p + 1)
              }
              className="px-5 py-2 text-[10px] font-black uppercase rounded-lg bg-slate-900 text-white border border-slate-700 disabled:opacity-20 hover:bg-slate-700 transition-all shadow-md"
            >
              Suivant
            </button>

          </div>

        </div>

      </div>

    </div>
  );
}